<template>
	<wrapper-features-templates-operations
	:scrollValue="scrollValue"
	:showLoader="showLoader"
	:showOverlayLoader="showOverlayLoader"
	:applyErrorState="applyErrorState && !showLoader"
	:hideContentWrapper="showAccountsEmptyState"
	:showStepper="activeStep <= stepperParams.length && showContent"
	:activeStep="activeStep"
	:stepperParams="stepperParams"
	:showFooter="activeStep < 3 && showContent && hasDebt"
	:footerButtonsParams="footerButtonsParams"
	:feedbackOperationData="feedbackOperationData"
	:tokenTitleAppend="t('cards.anticipatePayment.tokenTitleAppend')"
    @challengeSuccessHandler="challengeSuccessHandler"
    @challengeErrorHandler="challengeErrorHandler"
	@clearScrollToValue="scrollValue = null"
	@backOrCloseClicked="emit('backOrCloseClicked')">
		<!-- *********************************************
		*                      Content                   *
		********************************************** -->
		<component
		v-if ="showContent"
		:is="getActiveStepComponent"
		:formData="formData"
		:getCardsList="getCardsList"
		:getAccountsList="getAccountsBalances"
		:responseData="responseData"
		:mandatoryList="mandatoryList"
		:showMandatoryErrors="showMandatoryErrors"
		:clearFormErrors="clearFormErrors"
		@scrollTo="scrollValue = $event"
		@formErrorsCleared="clearFormErrors = false"
		@resetShowMandatoryErrorsProp="showMandatoryErrors = false"
		@updateData="updateDataHandler"
		@newPayment="newPaymentHandler"/>
		<!-- Empty-state -->
		<template #empty-state-slot>
			<transition name="common-enter-animation" mode="out-in" appear>
				<empty-state-generic
				v-if="showAccountsEmptyState"
				illustration="emptyState/cards-multicaixa"
				:title="t('cards.anticipatePayment.emptyState.title')"
				:description="t('cards.anticipatePayment.emptyState.description')"/>
			</transition>
		</template>
	</wrapper-features-templates-operations>
	<!-- Feedback Toasts -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, watch, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Aux */
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { atLeastOneIsEmpty, clearObjectStrings } from '@/assets/scripts/objects'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

const props = defineProps({
	selectedCardId: [String, Number]
})

/* mapActions & mapGetters */
const { requestCards, requestAccountsBalances, anticipatePayment, clearFeatureStates } = mapActions(moduleMapper.CARDS_ANTICIPATE_PAYMENT)
const { getAccountsBalances, getCardsList } = mapGetters(moduleMapper.CARDS_ANTICIPATE_PAYMENT)

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const showAccountsEmptyState = computed(() => {	return !showLoader.value && !applyErrorState.value && !getAccountsBalances.value.length && !getCardsList.value.length })
const showContent = computed(() => { return !showLoader.value && !applyErrorState.value && !!getAccountsBalances.value.length && !!getCardsList.value.length })

/* Data */
const applyErrorState = ref(false)
const scrollValue = ref(null)
const hasDebt = computed(() => { return formData.value.amountInDebt > 0 })
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

/*************************************************
*                   Steps Logic                  *
*************************************************/
const activeStep = ref(1)

const stepperParams = ref([
	{ description: t('cards.anticipatePayment.stepper.data') },
	{ description: t('cards.anticipatePayment.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
	const stepNumber = activeStep.value
	return defineAsyncComponent(() => import('@/components/features/cards/anticipatePayment/Step' + stepNumber))
})

const goToStep = stepNumber => {
	activeStep.value = stepNumber
	scrollValue.value = 0
}

const newPaymentHandler = () => {
	showOverlayLoader.value = true
	goToStep(1)
	clearFormData()
	initialRequests()
}

/*************************************************
*                Form Logic Data                 *
*************************************************/
const formData = ref({
	cardNumber: '',
	amountInDebt: '',
	accountId: '',
	accountDisplayInfo: '',
	currency: '',
	amount: ''
})

const mandatoryList = ref([
	'cardNumber',
	'accountId',
	'amount'
])

const updateDataHandler = ({ fieldName, inputValue }) => {
	formData.value[fieldName] = inputValue

	if (fieldName === 'cardNumber') {
		formData.value.amountInDebt = getCardsList.value.find(card => card.id === inputValue).amountInDebt
	}
}

/*************************************************
*             Submit Transfer Logic              *
*************************************************/
const submitPayment = async () => {
	showOverlayLoader.value = true

	const payload = {
		cardNumber: formData.value.cardNumber,
		accountId: formData.value.accountId,
		currency: formData.value.currency,
		amount: formData.value.amount
	}

	try {
		await anticipatePayment(payload)
		showOverlayLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, submitPayment)
	}
}

/*************************************************
*             Success/Error Handlers             *
*************************************************/
const feedbackOperationData = ref(null)
const responseData = ref()

const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const challengeSuccessHandler = async data => {
	responseData.value = {
		...data.data,
		errorLevel: data.errorLevel,
		code: data.code
	}

	feedbackOperationData.value = {
		status: data.errorLevel,
		title: data.message,
		additionalMessage: data.additionalMessage || '',
		buttonsData: !data.data.hasReceipt ? null : {
			emailAction: 'sendOperationEmailNotification',
			smsAction: 'sendOperationSMSNotification',
			sendPayload: { operationId: data.data.operationId },
			downloadUrl: createDownloadGenericReceiptPdf({ params: { operationID: data.data.operationId } }),
			shareLink: data.data.operationId,
			operationId: data.data.operationId
		}
	}

	goToStep(3)
}

const challengeErrorHandler = response => {
	feedbackToastData.value = errorObjectConstructor(response, () => { goToStep(2) })

	showFeedbackToast.value = true
	showOverlayLoader.value = false
}

const errorHandler = (data, retryFn) => {
	showLoader.value = false
	showOverlayLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

/*************************************************
*               Footer/Buttons Logic             *
*************************************************/
const validateFormValues = () => {
	formData.value.amount <= 0 || atLeastOneIsEmpty(formData.value, mandatoryList.value) ? showMandatoryErrors.value = true : goToStep(2)
}

/* Footer Buttons Data */
const footerButtonsData = computed(() => {
	return [{
		label: t('cards.anticipatePayment.btn.clear'),
		buttonPosition: 'left',
		hidden: activeStep.value !== 1,
		callbackFunction: () => { clearFormData() },
		step: 1
	}, {
		label: t('cards.anticipatePayment.btn.forward'),
		icon: 'arrow-right-full',
		buttonType: 'primary',
		iconPosition: 'right',
		hidden: activeStep.value !== 1,
		callbackFunction: () => { validateFormValues() }
	}, {
		label: t('cards.anticipatePayment.btn.edit'),
		icon: 'arrow-left-full',
		hidden: activeStep.value !== 2,
		callbackFunction: () => { goToStep(1) }
	}, {
		label: t('cards.anticipatePayment.btn.validate'),
		icon: 'check-line',
		buttonType: 'primary',
		iconPosition: 'right',
		hidden: activeStep.value !== 2,
		callbackFunction: () => { submitPayment() }
	}]
})

const footerButtonsParams = computed(() => {
	return footerButtonsData.value.filter(ele => !ele.hidden)
})

const clearFormData = () => {
	clearFormErrors.value = true
	clearObjectStrings(formData.value)

	/* Reset form */
	formData.value = {
		...formData.value,
		cardNumber: getCardsList.value[0].id,
		amountInDebt: getCardsList.value[0].amountInDebt,
		accountId: getAccountsBalances.value.find(account => account.default).id,
		accountDisplayInfo: getAccountsBalances.value.find(account => account.default).accountNumber,
		currency: getAccountsBalances.value.find(account => account.default).currency,
		amount: formData.value.amount
	}

	feedbackOperationData.value = null
	responseData.value = null
	showOverlayLoader.value = false
}

/*************************************************
*                Initial Requets                 *
*************************************************/
const initialRequests = async () => {
	applyErrorState.value = false
	showLoader.value = true

	/* We are using a Promise all to save sometime, calling all api's at the same time, since they dont depend on eachother */
	Promise.all([
	requestCards(),
	requestAccountsBalances()
	]).then(() => {
		if (getCardsList.value.length) {
			formData.value.cardNumber = props.selectedCardId ?? getCardsList.value[0].id
			formData.value.amountInDebt = props.selectedCardId ? getCardsList.value.find(card => card.id === props.selectedCardId).amountInDebt : getCardsList.value[0].amountInDebt
		}
		if (getAccountsBalances.value.length) {
			/* Setting Initial Selected Account */
			formData.value.accountId = getAccountsBalances.value.find(account => account.default).id
			/* Setting Initial Account Display Info from Default Account */
			formData.value.accountDisplayInfo = getAccountsBalances.value.find(account => account.default).accountNumber
			/* Setting Initial Currency from Default Account */
			formData.value.currency = getAccountsBalances.value.find(account => account.default).currency
		}
		showLoader.value = false
		goToStep(1)
	}).catch(error => {
		applyErrorState.value = true
		if (error.toString() === 'Cancel') return
		errorHandler(error.response.data, initialRequests)
	})
}

watch(() => activeStep.value, (newVal, oldVal) => {
	scrollValue.value = 0
})

initialRequests()

onBeforeUnmount(() => {
	clearFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
